import React, { useState, useEffect } from "react";
import { WifiOff, ZapOff, ArrowRight, X } from "lucide-react";
import { useI18n } from "../../i18n/I18nContext.js";

export interface NetworkBannerProps {
  onNavigateOffline?: () => void;
}

export const NetworkBanner: React.FC<NetworkBannerProps> = ({ onNavigateOffline }) => {
  const { language } = useI18n();
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== "undefined" ? !navigator.onLine : false
  );
  const [isSlowConnection, setIsSlowConnection] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setIsDismissed(false);
    };

    const handleOffline = () => {
      setIsOffline(true);
      setIsDismissed(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Network Information API check for 2G / slow connections
    const connection = (navigator as any).connection;
    if (connection) {
      const checkConnection = () => {
        const type = connection.effectiveType;
        if (type === "2g" || type === "slow-2g" || connection.saveData) {
          setIsSlowConnection(true);
        } else {
          setIsSlowConnection(false);
        }
      };

      checkConnection();
      connection.addEventListener?.("change", checkConnection);

      return () => {
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
        connection.removeEventListener?.("change", checkConnection);
      };
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  if (isDismissed || (!isOffline && !isSlowConnection)) {
    return null;
  }

  const translations = {
    en: {
      offlineTitle: "You're currently offline",
      offlineDesc: "Watch saved videos without an internet connection.",
      slowTitle: "Slow network detected",
      slowDesc: "Switch to offline library or data-saver mode for smooth streaming.",
      cta: "Open Offline Library",
    },
    fr: {
      offlineTitle: "Vous êtes actuellement hors ligne",
      offlineDesc: "Regardez vos vidéos enregistrées sans connexion internet.",
      slowTitle: "Connexion lente détectée",
      slowDesc: "Passez à la bibliothèque hors ligne pour un streaming fluide.",
      cta: "Ouvrir la bibliothèque hors ligne",
    },
    ln: {
      offlineTitle: "Ozali na réseau te",
      offlineDesc: "Tala bavidéyo obombaki kozanga internet.",
      slowTitle: "Réseau ezali na mbangu moke",
      slowDesc: "Kende na bibiothèque ya bavidéyo obombaki mpo etambola malamu.",
      cta: "Fungola bavidéyo ya libombo",
    },
    sw: {
      offlineTitle: "Haujaunganishwa kwenye mtandao",
      offlineDesc: "Tazama video zilizohifadhiwa bila mtandao.",
      slowTitle: "Mtandao una kasi ya chini",
      slowDesc: "Fungua maktaba ya video za nje ya mtandao kutazama vizuri.",
      cta: "Fungua Maktaba ya Nje ya Mtandao",
    },
  }[language] || {
    offlineTitle: "You're currently offline",
    offlineDesc: "Watch saved videos without an internet connection.",
    slowTitle: "Slow network detected",
    slowDesc: "Switch to offline library for smooth streaming.",
    cta: "Open Offline Library",
  };

  const handleCtaClick = () => {
    if (onNavigateOffline) {
      onNavigateOffline();
    } else if (typeof window !== "undefined") {
      try {
        window.history.pushState({}, "", "/offline-library");
        window.dispatchEvent(new PopStateEvent("popstate"));
      } catch {}
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-50 max-w-md bg-zinc-950/95 border border-zinc-800 text-white p-3.5 sm:p-4 rounded-2xl shadow-2xl backdrop-blur-md flex items-center justify-between gap-3 animate-in fade-in slide-in-from-bottom-3 duration-200"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
            isOffline
              ? "bg-rose-500/10 text-rose-400 border border-rose-500/20"
              : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
          }`}
        >
          {isOffline ? <WifiOff className="w-5 h-5" /> : <ZapOff className="w-5 h-5" />}
        </div>
        <div className="min-w-0">
          <h4 className="text-xs font-bold text-white truncate">
            {isOffline ? translations.offlineTitle : translations.slowTitle}
          </h4>
          <p className="text-[11px] text-zinc-400 line-clamp-1">
            {isOffline ? translations.offlineDesc : translations.slowDesc}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={handleCtaClick}
          className="px-3 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-xs font-bold flex items-center gap-1 transition-all active:scale-95 shadow-md shadow-emerald-500/20 whitespace-nowrap"
        >
          <span>{translations.cta}</span>
          <ArrowRight className="w-3 h-3" />
        </button>

        <button
          onClick={() => setIsDismissed(true)}
          className="p-1 rounded-lg text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors"
          title="Dismiss"
          aria-label="Dismiss notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default NetworkBanner;
